/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { Skeleton, Button, Tag, Row, Dot, DropdownMenu, DropdownMenuItem } from 'components/Blocks';
import { TagIcon } from 'components/Icons';

import { useProjectProfile } from './context';
import { sortByName } from 'utils/array';
import { cn } from 'styles';

interface TagBase {
  color: string;
  id: string;
  name: string;
}

interface TagSelectProps {
  children?: never;
  showAddButton: boolean;
  tags: Array<TagBase>;
  tagOptions: Array<TagBase>;
  onTagClick: (tag: TagBase) => Promise<any>;
  deleteTag: (tagId: string) => Promise<any>;
  tagClassName?: string;
}

export function TagSelect({
  showAddButton,
  tags,
  tagOptions,
  onTagClick,
  deleteTag,
  tagClassName,
}: TagSelectProps): JSX.Element {
  const [tagsBeingAdded, setTagsBeingAdded] = useState(0);

  const { fullBlocked } = useProjectProfile();

  const { t } = useLang();

  const handleClick = async (tag: TagBase): Promise<void> => {
    setTagsBeingAdded((old) => old + 1);

    await onTagClick(tag);

    setTagsBeingAdded((old) => old - 1);
  };

  const remainingTags = useMemo(() => {
    return sortByName(tagOptions.filter(({ id }) => !tags.find((tag) => tag.id === id)));
  }, [tagOptions, tags]);

  const reallyShowButton = showAddButton && tagOptions.length !== tags.length && !fullBlocked;

  return (
    <Row flexWrap="wrap" gap="2">
      {sortByName(tags).map((tag) => (
        <Tag
          key={`${tag.id}-ask-modal`}
          size="medium"
          className={cn('min-h-7', tagClassName)}
          action={showAddButton ? () => deleteTag(tag.id) : undefined}
          {...tag}
        />
      ))}

      {!!tagsBeingAdded &&
        Array.from({ length: tagsBeingAdded }).map((_, index) => (
          <Skeleton key={`loading-tag-modal=-${index}`} height={30} width={120} />
        ))}

      {reallyShowButton && (
        <DropdownMenu
          className="max-h-96 overflow-auto"
          align="start"
          trigger={
            <Button
              size="small"
              variant="ghost"
              format="square"
              className="text-primary dark:text-card-foreground"
              round
              tooltipContent={t('add-tags')}
            >
              <TagIcon />
            </Button>
          }
        >
          {remainingTags.map((tag) => (
            <DropdownMenuItem
              key={tag.id}
              onClick={() => handleClick(tag)}
              className="cursor-pointer"
            >
              <Dot color={tag.color} />

              <span>{tag.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      )}
    </Row>
  );
}
